import { useState } from 'react';
import GroupPicker from './GroupPicker';
import { cn, ui } from '../lib/ui';
import {
  ErrorAlert,
  Field,
  SelectField,
  TextareaField,
  UserSearchPicker
} from './ui';

const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'];
const UNIT_PLURALS = { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' };
const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function toLocalInput(iso) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return (
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    `T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  );
}

export function roundToNextHalfHour() {
  const now = new Date();
  const minutes = now.getMinutes();
  const start = new Date(now);
  start.setMinutes(minutes <= 30 ? 30 : 60, 0, 0);
  return start;
}

export function emptyPersonalForm() {
  const start = roundToNextHalfHour();
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    subject: '',
    startTime: toLocalInput(start.toISOString()),
    endTime: toLocalInput(end.toISOString()),
    location: '',
    message: '',
    attendees: [],
    sharedWithUsers: [],
    sharedWithGroups: [],
    visibility: 'private',
    eventType: 'personal',
    frequency: 'none',
    interval: '1',
    daysOfWeek: [],
    dayOfMonth: '',
    recurrenceEndDate: '',
    occurrenceCount: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    showTimeAs: 'busy'
  };
}

export function buildRecurrence(form) {
  if (form.frequency === 'none') return null;
  const rule = {
    frequency: form.frequency,
    interval: Math.max(1, Number(form.interval) || 1)
  };
  if (form.frequency === 'weekly' && form.daysOfWeek.length) rule.daysOfWeek = form.daysOfWeek;
  if (form.frequency === 'monthly' && form.dayOfMonth) rule.dayOfMonth = Number(form.dayOfMonth);
  if (form.recurrenceEndDate) rule.endDate = new Date(form.recurrenceEndDate).toISOString();
  else if (form.occurrenceCount) rule.occurrenceCount = Number(form.occurrenceCount);
  return rule;
}

export function buildPersonalPayload(form, forUpdate) {
  const recurrence = buildRecurrence(form);
  const payload = {
    subject: form.subject,
    startTime: new Date(form.startTime).toISOString(),
    endTime: new Date(form.endTime).toISOString(),
    timezone: form.timezone,
    showTimeAs: form.showTimeAs,
    location: form.location || undefined,
    message: form.message || undefined,
    attendees: form.attendees.map((u) => u.userId),
    visibility: form.visibility,
    eventType: form.eventType,
    sharedWith: { users: form.sharedWithUsers.map((u) => u.userId), groups: form.sharedWithGroups }
  };
  if (recurrence || forUpdate) payload.recurrence = recurrence;
  return payload;
}

export function timeOrRecurrenceChanged(form, initial) {
  if (!initial) return false;
  if (form.startTime !== initial.startTime || form.endTime !== initial.endTime) return true;
  return JSON.stringify(buildRecurrence(form)) !== JSON.stringify(buildRecurrence(initial));
}

export function scheduleToForm(schedule, resolvedAttendees, resolvedSharedUsers) {
  const sw = schedule.sharedWith || { users: [], groups: [] };
  const hasAttendees = (schedule.attendees || []).length > 0;
  const hasSharedUsers = (sw.users || []).length > 0;
  const hasSharedGroups = (sw.groups || []).length > 0;
  const isShared = schedule.visibility === 'shared' || hasAttendees || hasSharedUsers || hasSharedGroups;
  return {
    subject: schedule.subject || '',
    startTime: toLocalInput(schedule.startTime),
    endTime: toLocalInput(schedule.endTime),
    location: schedule.location || '',
    message: schedule.message || '',
    attendees: resolvedAttendees || [],
    sharedWithUsers: resolvedSharedUsers || [],
    sharedWithGroups: sw.groups || [],
    visibility: isShared ? 'shared' : (schedule.visibility || 'private'),
    eventType: schedule.eventType || (hasAttendees ? 'invitation' : 'personal'),
    frequency: (schedule.recurrence && schedule.recurrence.frequency) || 'none',
    interval: String((schedule.recurrence && schedule.recurrence.interval) || 1),
    daysOfWeek: (schedule.recurrence && schedule.recurrence.daysOfWeek) || [],
    dayOfMonth: (schedule.recurrence && schedule.recurrence.dayOfMonth) ? String(schedule.recurrence.dayOfMonth) : '',
    recurrenceEndDate: (schedule.recurrence && schedule.recurrence.endDate) ? schedule.recurrence.endDate.slice(0, 10) : '',
    occurrenceCount: (schedule.recurrence && schedule.recurrence.occurrenceCount) ? String(schedule.recurrence.occurrenceCount) : '',
    timezone: schedule.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    showTimeAs: schedule.showTimeAs || 'busy'
  };
}

export default function PersonalEventForm({ initial, submitLabel, onSubmit, onCancel, warnOnTimeChange = false }) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(() => initial || emptyPersonalForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const showResetWarning = warnOnTimeChange && timeOrRecurrenceChanged(form, initial);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    if (form.endTime && form.startTime && form.endTime <= form.startTime) {
      setError(new Error('End time must be after start time.'));
      return;
    }
    const startMin = form.startTime ? new Date(form.startTime).getMinutes() : 0;
    const endMin = form.endTime ? new Date(form.endTime).getMinutes() : 0;
    if (startMin % 15 !== 0 || endMin % 15 !== 0) {
      setError(new Error('Start and end times must align to 15-minute intervals (e.g. :00, :15, :30, :45).'));
      return;
    }
    setBusy(true);
    try {
      await onSubmit(buildPersonalPayload(form, warnOnTimeChange));
      if (!isEdit) setForm(emptyPersonalForm());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <ErrorAlert error={error} />
      <Field
        label="Subject"
        required
        value={form.subject}
        onChange={(e) => set({ subject: e.target.value })}
      />
      <Field
        label="Starts"
        type="datetime-local"
        required
        step="900"
        value={form.startTime}
        onChange={(e) => {
          const next = { startTime: e.target.value };
          if (!form.endTime || form.endTime <= e.target.value) {
            const s = new Date(e.target.value);
            if (!Number.isNaN(s.getTime())) {
              next.endTime = toLocalInput(new Date(s.getTime() + 60 * 60 * 1000).toISOString());
            }
          }
          set(next);
        }}
      />
      <Field
        label="Ends"
        type="datetime-local"
        required
        step="900"
        value={form.endTime}
        onChange={(e) => set({ endTime: e.target.value })}
      />
      <Field
        label="Location (optional)"
        value={form.location}
        onChange={(e) => set({ location: e.target.value })}
      />
      <fieldset className={ui.field}>
        <legend className={ui.label}>Invite attendees</legend>
        <p className={ui.hint}>Invited attendees are notified and can accept, decline, or reply tentative.</p>
        {form.attendees.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-1">
            {form.attendees.map((u) => (
              <span key={u.userId} className={cn(ui.pillNeutral, 'gap-1')}>
                {u.displayName || u.email || u.userId}
                <button
                  type="button"
                  onClick={() => {
                    const next = form.attendees.filter((a) => a.userId !== u.userId);
                    const patch = { attendees: next };
                    if (!next.length && !form.sharedWithUsers.length && !form.sharedWithGroups.length) {
                      patch.visibility = 'private';
                      patch.eventType = 'personal';
                    }
                    set(patch);
                  }}
                  className="cursor-pointer border-0 bg-transparent p-0 leading-none text-current"
                  aria-label={`Remove ${u.displayName || u.email || u.userId}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <UserSearchPicker
          exclude={form.attendees.map((u) => u.userId)}
          onPick={(u) => {
            set({
              attendees: [...form.attendees, u],
              visibility: 'shared',
              eventType: 'invitation'
            });
          }}
        />
      </fieldset>

      <SelectField
        label="Visibility"
        hint="Private events are visible only to you. Shared events appear on invitees' calendars."
        value={form.visibility}
        onChange={(e) => {
          const vis = e.target.value;
          const patch = { visibility: vis };
          if (vis === 'shared') patch.eventType = form.attendees.length ? 'invitation' : 'personal';
          else patch.eventType = 'personal';
          set(patch);
        }}
      >
        <option value="private">Private (only me)</option>
        <option value="shared">Shared (visible to invitees)</option>
      </SelectField>

      {form.visibility === 'shared' && (
        <>
          <fieldset className={ui.field}>
            <legend className={ui.label}>Share with (viewers)</legend>
            <p className={ui.hint}>These members can see the event but are not asked to RSVP.</p>
            {form.sharedWithUsers.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-1">
                {form.sharedWithUsers.map((u) => (
                  <span key={u.userId} className={cn(ui.pillNeutral, 'gap-1')}>
                    {u.displayName || u.email}
                    <button
                      type="button"
                      onClick={() => set({ sharedWithUsers: form.sharedWithUsers.filter((a) => a.userId !== u.userId) })}
                      className="cursor-pointer border-0 bg-transparent p-0 leading-none text-current"
                      aria-label={`Remove ${u.displayName || u.email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <UserSearchPicker
              exclude={[...form.attendees.map((u) => u.userId), ...form.sharedWithUsers.map((u) => u.userId)]}
              onPick={(u) => set({ sharedWithUsers: [...form.sharedWithUsers, u] })}
            />
          </fieldset>

          <fieldset className={ui.field}>
            <legend className={ui.label}>Share with groups</legend>
            <p className={ui.hint}>All members of selected groups will see this event on their calendar.</p>
            <GroupPicker
              selected={form.sharedWithGroups}
              onChange={(groups) => set({ sharedWithGroups: groups })}
            />
          </fieldset>
        </>
      )}

      <TextareaField
        label="Message (optional)"
        rows={3}
        value={form.message}
        onChange={(e) => set({ message: e.target.value })}
      />

      <SelectField
        label="Show time as"
        value={form.showTimeAs}
        onChange={(e) => set({ showTimeAs: e.target.value })}
      >
        <option value="busy">Busy</option>
        <option value="free">Free</option>
        <option value="tentative">Tentative</option>
        <option value="out-of-office">Out of office</option>
      </SelectField>

      <SelectField
        label="Repeats"
        value={form.frequency}
        onChange={(e) => set({ frequency: e.target.value })}
      >
        <option value="none">does not repeat</option>
        {FREQUENCIES.map((frequency) => (
          <option key={frequency} value={frequency}>
            {frequency}
          </option>
        ))}
      </SelectField>

      {form.frequency !== 'none' && (
        <>
          <Field
            label={`Every N ${UNIT_PLURALS[form.frequency]}`}
            type="number"
            min="1"
            value={form.interval}
            onChange={(e) => set({ interval: e.target.value })}
          />
          {form.frequency === 'weekly' && (
            <fieldset className={ui.field}>
              <legend className={ui.label}>On days</legend>
              <div className={ui.row}>
                {WEEKDAY_NAMES.map((day) => (
                  <label key={day} className="text-body-s">
                    <input
                      type="checkbox"
                      checked={form.daysOfWeek.includes(day)}
                      onChange={(e) =>
                        set({
                          daysOfWeek: e.target.checked
                            ? [...form.daysOfWeek, day]
                            : form.daysOfWeek.filter((d) => d !== day)
                        })
                      }
                    />{' '}
                    {day.slice(0, 3)}
                  </label>
                ))}
              </div>
              <span className={ui.hint}>
                Leave empty to repeat on the same weekday as the start date.
              </span>
            </fieldset>
          )}
          {form.frequency === 'monthly' && (
            <Field
              label="Day of month (optional)"
              type="number"
              min="1"
              max="31"
              hint="Months without that day are skipped - day 31 never lands in April."
              value={form.dayOfMonth}
              onChange={(e) => set({ dayOfMonth: e.target.value })}
            />
          )}
          <Field
            label="Ends on (optional)"
            type="date"
            hint="Leave empty to end after a number of occurrences instead - or never."
            value={form.recurrenceEndDate}
            onChange={(e) => set({ recurrenceEndDate: e.target.value })}
          />
          {!form.recurrenceEndDate && (
            <Field
              label="Number of occurrences (optional)"
              type="number"
              min="1"
              hint="Counted from the first occurrence, which is the start date above."
              value={form.occurrenceCount}
              onChange={(e) => set({ occurrenceCount: e.target.value })}
            />
          )}
        </>
      )}

      {showResetWarning && (
        <div className={ui.alertInfo} role="status">
          Changing the time or recurrence resets everyone&rsquo;s responses to pending and
          re-notifies attendees.
        </div>
      )}

      <div className={cn(ui.row, 'mt-4')}>
        <button type="submit" className={ui.btnPrimary} disabled={busy}>
          {busy ? 'Saving…' : (submitLabel || 'Create event')}
        </button>
        {onCancel && (
          <button type="button" className={ui.btnSecondary} disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
